import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatMoney, formatDate, monthKey, monthLabel } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CATEGORY_COLORS } from "@/lib/colors";
import { Kicker, LoadingCard, ErrorCard, EmptyState, StatusPill } from "@/components/common";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2 } from "lucide-react";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "pending", label: "Pending" },
  { id: "approved", label: "Approved" },
  { id: "rejected", label: "Rejected" },
];

export default function ExpensesList() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/expenses");
      setRows(res.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load expenses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return rows;
    return rows.filter((r) => r.status === filter);
  }, [rows, filter]);

  const grouped = useMemo(() => {
    const map = {};
    for (const r of filtered) {
      const k = monthKey(r.date);
      if (!map[k]) map[k] = { key: k, label: monthLabel(k), items: [], totals: { CAD: 0, USD: 0 } };
      map[k].items.push(r);
      map[k].totals[r.currency] = (map[k].totals[r.currency] || 0) + Number(r.amount);
    }
    return Object.values(map).sort((a, b) => (a.key < b.key ? 1 : -1));
  }, [filtered]);

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-6 py-8">
        <LoadingCard />
      </div>
    );
  }
  if (error) {
    return (
      <div className="max-w-6xl mx-auto px-6 py-8">
        <ErrorCard message={error} onRetry={load} />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-8 animate-fade-up">
      <div className="flex items-end justify-between mb-6">
        <div>
          <Kicker>Expenses</Kicker>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-zinc-950 mt-1">
            {user?.role === "manager" ? "All team expenses" : "Your expenses"}
          </h1>
        </div>
        <Link
          to="/add"
          data-testid="expenses-new-btn"
          className="inline-flex items-center gap-2 rounded-lg bg-zinc-950 text-white px-4 py-2.5 text-sm font-medium hover:bg-zinc-800"
        >
          <Plus className="w-4 h-4" /> New expense
        </Link>
      </div>

      <div className="inline-flex rounded-lg border border-zinc-200 bg-white p-1 text-sm mb-6">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            data-testid={`filter-${f.id}`}
            onClick={() => setFilter(f.id)}
            className={`px-3 py-1.5 rounded-md transition-colors ${
              filter === f.id ? "bg-zinc-950 text-white" : "text-zinc-600 hover:text-zinc-900"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {rows.length === 0 ? (
        <EmptyState
          title="No expenses yet"
          description={user?.role === "manager" ? "Nothing has been submitted by the team yet." : "Submit your first expense to see it here."}
          action={
            <Link to="/add" data-testid="empty-add-btn" className="inline-flex items-center gap-2 rounded-lg bg-zinc-950 text-white px-4 py-2 text-sm font-medium hover:bg-zinc-800">
              <Plus className="w-4 h-4" /> New expense
            </Link>
          }
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          title={`No ${filter} expenses`}
          description="Try switching the filter or clearing it to view all."
        />
      ) : (
        <div className="space-y-8">
          {grouped.map((g) => (
            <section key={g.key} data-testid={`month-section-${g.key}`}>
              <div className="flex items-end justify-between mb-3">
                <div>
                  <Kicker>{g.label}</Kicker>
                  <h3 className="font-display text-lg font-medium text-zinc-950 mt-0.5">
                    {g.items.length} {g.items.length === 1 ? "expense" : "expenses"}
                  </h3>
                </div>
                <div className="text-sm text-zinc-700 tabular-nums">
                  {g.totals.CAD > 0 ? <span>{formatMoney(g.totals.CAD, "CAD")}</span> : null}
                  {g.totals.CAD > 0 && g.totals.USD > 0 ? <span className="text-zinc-300 mx-2">·</span> : null}
                  {g.totals.USD > 0 ? <span>{formatMoney(g.totals.USD, "USD")}</span> : null}
                </div>
              </div>
              <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-zinc-500 text-xs uppercase tracking-wider">
                      <th className="px-5 py-3 font-medium">Date</th>
                      {user?.role === "manager" ? <th className="px-5 py-3 font-medium">Employee</th> : null}
                      <th className="px-5 py-3 font-medium">Merchant</th>
                      <th className="px-5 py-3 font-medium">Category</th>
                      <th className="px-5 py-3 font-medium">Customer</th>
                      <th className="px-5 py-3 font-medium text-right">Amount</th>
                      <th className="px-5 py-3 font-medium">Status</th>
                      <th className="px-5 py-3 font-medium text-right w-10" aria-label="Actions"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.items.map((e) => {
                      const isOwner = user?.id === e.user_id;
                      const canModify = user?.role === "manager" || (isOwner && e.status === "pending");
                      return (
                      <tr
                        key={e.id}
                        data-testid={`expense-row-${e.id}`}
                        className="border-t border-zinc-100 hover:bg-zinc-50 cursor-pointer"
                        onClick={() => (window.location.href = `/expense/${e.id}`)}
                      >
                        <td className="px-5 py-4 text-zinc-700 whitespace-nowrap">{formatDate(e.date)}</td>
                        {user?.role === "manager" ? (
                          <td className="px-5 py-4 text-zinc-700">{e.user_name}</td>
                        ) : null}
                        <td className="px-5 py-4 text-zinc-900 font-medium">{e.merchant || "—"}</td>
                        <td className="px-5 py-4 text-zinc-700">
                          {(() => {
                            const c = CATEGORY_COLORS[e.category] || CATEGORY_COLORS.Other;
                            return (
                              <span
                                className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-medium border ${c.bg} ${c.text} ${c.border}`}
                              >
                                <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
                                {e.category}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-5 py-4 text-zinc-700">{e.customer || "—"}</td>
                        <td className="px-5 py-4 text-right text-zinc-900 font-medium tabular-nums">
                          {formatMoney(e.amount, e.currency)}
                        </td>
                        <td className="px-5 py-4">
                          <StatusPill status={e.status} />
                        </td>
                        <td className="px-3 py-4 text-right" onClick={(ev) => ev.stopPropagation()}>
                          {canModify ? (
                            <RowDeleteButton
                              expense={e}
                              onDeleted={() => setRows((prev) => prev.filter((r) => r.id !== e.id))}
                            />
                          ) : null}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function RowDeleteButton({ expense, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const handle = async () => {
    setBusy(true);
    try {
      await api.delete(`/expenses/${expense.id}`);
      onDeleted();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button
          data-testid={`row-delete-btn-${expense.id}`}
          onClick={(ev) => ev.stopPropagation()}
          className="p-1.5 rounded-md text-zinc-400 hover:text-red-600 hover:bg-red-50"
          title="Delete expense"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent onClick={(ev) => ev.stopPropagation()}>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this expense?</AlertDialogTitle>
          <AlertDialogDescription>
            {expense.merchant ? `${expense.merchant} · ` : ""}
            {formatMoney(expense.amount, expense.currency)} on {formatDate(expense.date)}.
            This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`row-delete-cancel-${expense.id}`}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            data-testid={`row-delete-confirm-${expense.id}`}
            onClick={handle}
            disabled={busy}
            className="bg-red-600 text-white hover:bg-red-700"
          >
            {busy ? "Deleting…" : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
